// Typed REST API v1 client. Types come from the Zod schemas in @platform/core.
import type {
  AuditLogEntryDto,
  BrandingConfig,
  ClientAccessDto,
  ClientBrandingDto,
  GroupDto,
  UserTypeDto,
  UserTypePolicy,
  CreateUserBody,
  CustomFieldDefinition,
  LoginMethodConfig,
  LogRetentionConfig,
  MeDto,
  OAuthClientDto,
  RegisterClientBody,
  RoleDto,
  SessionDto,
  UpdateUserBody,
  UserDetailDto,
  UserDto,
  UserIdentifierDto,
  IdentifierType,
  Permission,
  UpdateClientBodySchema,
} from "@platform/core";
import type { z } from "zod";
import { ApiError, toApiError } from "./errors";

export interface Page<T> {
  items: T[];
  nextCursor?: string;
  total?: number;
}

export interface OverviewStats {
  totalUsers: number;
  active24h: number;
  active7d: number;
  signInsToday: number;
  signInsPerDay: Array<{ date: string; count: number }>;
  failedSignIns24h: number;
  mfaAdoptionPercent: number;
}

export interface SettingEnvelope<T> {
  key: string;
  value: T;
  version: number;
  updatedAt: string;
  updatedBy?: string;
  etag: string;
}

export interface ProviderStatus {
  id: string;
  configured: boolean;
  supported: boolean;
}

export interface ExportJobDto {
  id: string;
  status: "pending" | "running" | "done" | "failed";
  scope: string;
  requestedBy: string;
  fileRef?: string;
  createdAt: string;
  finishedAt?: string;
  error?: string;
}

export interface SettingValues {
  "login-methods": LoginMethodConfig;
  "custom-fields": CustomFieldDefinition[];
  branding: BrandingConfig;
  "log-retention": LogRetentionConfig;
}

export interface ApiClientOptions {
  baseUrl: string;
  getToken: () => Promise<string | null>;
  getLocale: () => string;
  /** Called for every failed request before it is thrown (global 401/403/429 handling). */
  onError?: (e: ApiError) => void;
  fetchImpl?: typeof fetch;
}

type Query = Record<string, string | number | boolean | undefined | null | string[]>;

export interface RequestOptions {
  query?: Query;
  body?: unknown;
  headers?: Record<string, string>;
  /** Suppress the global error hook (caller handles it). */
  silent?: boolean;
}

export function buildQuery(q: Query | undefined): string {
  if (!q) return "";
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) {
      if (v.length) p.set(k, v.join(","));
    } else p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

export function createApiClient(opts: ApiClientOptions) {
  const f = opts.fetchImpl ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const base = opts.baseUrl.replace(/\/$/, "");

  async function raw(method: string, path: string, o: RequestOptions = {}): Promise<Response> {
    const headers = new Headers(o.headers);
    headers.set("accept", "application/json");
    headers.set("accept-language", opts.getLocale());
    const token = await opts.getToken();
    if (token) headers.set("authorization", `Bearer ${token}`);
    let body: BodyInit | undefined;
    if (o.body !== undefined) {
      headers.set("content-type", "application/json");
      body = JSON.stringify(o.body);
    }
    let res: Response;
    try {
      res = await f(`${base}/api/v1${path}${buildQuery(o.query)}`, {
        method,
        headers,
        ...(body !== undefined ? { body } : {}),
      });
    } catch {
      const e = new ApiError({ status: 0, code: "NETWORK", title: "Network error" });
      if (!o.silent) opts.onError?.(e);
      throw e;
    }
    if (!res.ok) {
      const e = await toApiError(res);
      if (!o.silent) opts.onError?.(e);
      throw e;
    }
    return res;
  }

  async function json<T>(method: string, path: string, o?: RequestOptions): Promise<T> {
    const res = await raw(method, path, o);
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  async function getSetting<K extends keyof SettingValues>(
    key: K,
  ): Promise<SettingEnvelope<SettingValues[K]>> {
    const res = await raw("GET", `/settings/${key}`);
    const body = (await res.json()) as Omit<SettingEnvelope<SettingValues[K]>, "etag">;
    return { ...body, etag: res.headers.get("etag") ?? `"${body.version}"` };
  }

  async function putSetting<K extends keyof SettingValues>(
    key: K,
    value: SettingValues[K],
    etag: string,
  ): Promise<SettingEnvelope<SettingValues[K]>> {
    const res = await raw("PUT", `/settings/${key}`, {
      body: { value },
      headers: { "if-match": etag },
      silent: true,
    });
    const body = (await res.json()) as Omit<SettingEnvelope<SettingValues[K]>, "etag">;
    return { ...body, etag: res.headers.get("etag") ?? `"${body.version}"` };
  }

  const enc = encodeURIComponent;

  return {
    raw,
    json,
    me: () => json<MeDto>("GET", "/me"),
    publicBranding: () => json<BrandingConfig>("GET", "/public/branding"),
    stats: () => json<OverviewStats>("GET", "/stats/overview"),
    users: {
      list: (q: {
        q?: string;
        status?: string;
        role?: string;
        mfa?: string;
        type?: string;
        group?: string;
        sort?: "createdAt" | "email" | "displayName";
        order?: "asc" | "desc";
        cursor?: string;
        limit?: number;
      }) => json<Page<UserDto>>("GET", "/users", { query: q }),
      get: (id: string) => json<UserDetailDto>("GET", `/users/${enc(id)}`),
      create: (body: CreateUserBody) => json<UserDetailDto>("POST", "/users", { body, silent: true }),
      update: (id: string, body: UpdateUserBody) =>
        json<UserDetailDto>("PATCH", `/users/${enc(id)}`, { body, silent: true }),
      remove: (id: string) => json<void>("DELETE", `/users/${enc(id)}`),
      ban: (id: string, reason: string) =>
        json<UserDto>("POST", `/users/${enc(id)}/ban`, { body: { reason } }),
      unban: (id: string) => json<UserDto>("POST", `/users/${enc(id)}/unban`),
      setRoles: (id: string, roles: string[]) =>
        json<UserDto>("PUT", `/users/${enc(id)}/roles`, { body: { roles } }),
      setGroups: (id: string, groups: string[]) =>
        json<UserDto>("PUT", `/users/${enc(id)}/groups`, { body: { groups }, silent: true }),
      sessions: (id: string) => json<{ items: SessionDto[] }>("GET", `/users/${enc(id)}/sessions`),
      revokeSession: (id: string, sid: string) =>
        json<void>("DELETE", `/users/${enc(id)}/sessions/${enc(sid)}`),
      revokeSessions: (id: string) => json<void>("DELETE", `/users/${enc(id)}/sessions`),
      resetMfa: (id: string) => json<void>("POST", `/users/${enc(id)}/mfa/reset`),
      sendPasswordReset: (id: string) => json<void>("POST", `/users/${enc(id)}/password-reset`),
      setPassword: (id: string, password?: string) =>
        json<{ password?: string; passwordChangeRequired: true }>("POST", `/users/${enc(id)}/password`, {
          body: password === undefined ? {} : { password },
          silent: true,
        }),
      exportData: (id: string) => json<{ records: unknown[] }>("GET", `/users/${enc(id)}/export`),
      identifiers: {
        list: (id: string) =>
          json<{ items: UserIdentifierDto[]; smsAvailable: boolean }>("GET", `/users/${enc(id)}/identifiers`),
        add: (id: string, body: { type: IdentifierType; value: string; verified?: boolean }) =>
          json<UserIdentifierDto>("POST", `/users/${enc(id)}/identifiers`, { body, silent: true }),
        remove: (id: string, iid: string) =>
          json<void>("DELETE", `/users/${enc(id)}/identifiers/${enc(iid)}`),
        makePrimary: (id: string, iid: string) =>
          json<void>("POST", `/users/${enc(id)}/identifiers/${enc(iid)}/primary`, { body: {} }),
        markVerified: (id: string, iid: string) =>
          json<void>("POST", `/users/${enc(id)}/identifiers/${enc(iid)}/verify`, { body: {} }),
      },
    },
    roles: {
      list: () => json<{ items: RoleDto[] }>("GET", "/roles"),
      create: (body: { name: string; description?: string; permissions: Permission[] }) =>
        json<RoleDto>("POST", "/roles", { body, silent: true }),
      update: (id: string, body: { description?: string; permissions?: Permission[] }) =>
        json<RoleDto>("PATCH", `/roles/${enc(id)}`, { body, silent: true }),
      remove: (id: string) => json<void>("DELETE", `/roles/${enc(id)}`),
    },
    userTypes: {
      list: () => json<{ items: UserTypeDto[] }>("GET", "/user-types"),
      create: (body: { id: string; name: string; description?: string; policy?: Partial<UserTypePolicy> }) =>
        json<UserTypeDto>("POST", "/user-types", { body, silent: true }),
      update: (
        id: string,
        body: { name?: string; description?: string | null; policy?: Partial<UserTypePolicy> },
      ) => json<UserTypeDto>("PATCH", `/user-types/${enc(id)}`, { body, silent: true }),
      remove: (id: string) => json<void>("DELETE", `/user-types/${enc(id)}`, { silent: true }),
    },
    groups: {
      list: () => json<{ items: GroupDto[] }>("GET", "/groups"),
      get: (id: string) => json<GroupDto>("GET", `/groups/${enc(id)}`),
      create: (body: { id: string; name: string; description?: string }) =>
        json<GroupDto>("POST", "/groups", { body, silent: true }),
      update: (id: string, body: { name?: string; description?: string | null }) =>
        json<GroupDto>("PATCH", `/groups/${enc(id)}`, { body, silent: true }),
      remove: (id: string) => json<void>("DELETE", `/groups/${enc(id)}`, { silent: true }),
      members: (id: string) => json<{ items: UserDto[] }>("GET", `/groups/${enc(id)}/members`),
      addMembers: (id: string, userIds: string[]) =>
        json<{ added: string[] }>("POST", `/groups/${enc(id)}/members`, { body: { userIds }, silent: true }),
      removeMember: (id: string, userId: string) =>
        json<void>("DELETE", `/groups/${enc(id)}/members/${enc(userId)}`, { silent: true }),
    },
    logs: {
      list: (q: {
        type?: string[];
        actor?: string;
        subject?: string;
        from?: string;
        to?: string;
        cursor?: string;
        limit?: number;
      }) => json<Page<AuditLogEntryDto>>("GET", "/audit-logs", { query: q }),
    },
    settings: {
      get: getSetting,
      put: putSetting,
      providers: () => json<{ items: ProviderStatus[] }>("GET", "/sign-in-providers"),
    },
    clients: {
      list: () => json<{ items: OAuthClientDto[] }>("GET", "/oauth-clients"),
      get: (id: string) => json<OAuthClientDto>("GET", `/oauth-clients/${enc(id)}`),
      register: (body: RegisterClientBody) =>
        json<{ client: OAuthClientDto; clientSecret?: string }>("POST", "/oauth-clients", {
          body,
          silent: true,
        }),
      update: (id: string, body: z.infer<typeof UpdateClientBodySchema>) =>
        json<OAuthClientDto>("PATCH", `/oauth-clients/${enc(id)}`, { body, silent: true }),
      setAccess: (id: string, access: ClientAccessDto) =>
        json<OAuthClientDto>("PATCH", `/oauth-clients/${enc(id)}`, { body: { access }, silent: true }),
      setBranding: (id: string, branding: ClientBrandingDto | null) =>
        json<OAuthClientDto>("PATCH", `/oauth-clients/${enc(id)}`, { body: { branding }, silent: true }),
      rotate: (id: string) =>
        json<{ clientSecret: string }>("POST", `/oauth-clients/${enc(id)}/rotate-secret`),
      disable: (id: string) => json<OAuthClientDto>("POST", `/oauth-clients/${enc(id)}/disable`),
      enable: (id: string) => json<OAuthClientDto>("POST", `/oauth-clients/${enc(id)}/enable`),
    },
    exports: {
      list: () => json<{ items: ExportJobDto[] }>("GET", "/exports"),
      start: (body: { includeAuditLogs: boolean; includeCredentials: boolean }) =>
        json<ExportJobDto>("POST", "/exports", { body }),
      download: async (id: string) => (await raw("GET", `/exports/${enc(id)}/download`)).blob(),
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
